import React, { useEffect, useRef, useState } from "react";
import { useQueueConfig } from "../hooks/useQueueConfig";
import { useQueueState } from "../hooks/useQueueState";
import { MediaZone } from "../components/MediaZone";
import { PasswordZone } from "../components/PasswordZone";
import { Ticker } from "../components/Ticker";
import { CardsLayout } from "../components/CardsLayout";
import { LAYOUT_MODES, TTS_LANGUAGES } from "../lib/constants";
import { pickVoice } from "../lib/tts";
import { startDuck, endDuck } from "../lib/mediaDuck";
import { useWakeLock } from "../lib/useWakeLock";
import { useScheduledReload } from "../lib/useScheduledReload";

const buildAnnouncement = (ticket, desk, lang, nomenclature) => {
  const found = TTS_LANGUAGES.find((l) => l.code === lang) || TTS_LANGUAGES[0];
  return found.template
    .replace("{ticket}", ticket)
    .replace("{desk}", desk)
    .replace("{nomenclature}", nomenclature || "Guichê");
};

const speak = (text, ttsCfg) => {
  if (!ttsCfg.enabled || typeof window.speechSynthesis === "undefined") return;
  window.speechSynthesis.cancel();
  const allVoices = window.speechSynthesis.getVoices() || [];
  const voice = pickVoice(allVoices, {
    lang: ttsCfg.lang,
    gender: ttsCfg.gender,
    voiceURI: ttsCfg.voiceURI,
  });
  const repeat = ttsCfg.repeat || 1;
  // Duck media for the entire utterance sequence.
  startDuck();
  let safetyEnded = false;
  const endOnce = () => {
    if (safetyEnded) return;
    safetyEnded = true;
    endDuck();
  };
  // Safety timeout in case onend doesn't fire (Chrome bug w/ long speech queues)
  const safetyMs = 2500 * repeat + 3000;
  const safetyId = window.setTimeout(endOnce, safetyMs);

  for (let i = 0; i < repeat; i++) {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = ttsCfg.lang;
    u.rate = ttsCfg.rate;
    u.pitch = ttsCfg.pitch;
    u.volume = ttsCfg.volume;
    if (voice) u.voice = voice;
    if (i === repeat - 1) {
      u.onend = () => {
        window.clearTimeout(safetyId);
        endOnce();
      };
      u.onerror = () => {
        window.clearTimeout(safetyId);
        endOnce();
      };
    }
    window.speechSynthesis.speak(u);
  }
};

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [880, 1175];
    notes.forEach((freq, idx) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + idx * 0.18);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + idx * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + idx * 0.18 + 0.25);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + idx * 0.18);
      o.stop(ctx.currentTime + idx * 0.18 + 0.3);
    });
  } catch {
    /* no-op */
  }
};

const TVDisplay = () => {
  const { state, onStateChange, lastPolledAt, configUpdatedAt } = useQueueState({ poll: true });
  const { config } = useQueueConfig({ refetchKey: configUpdatedAt });
  const flashRef = useRef(null);
  const ttsRef = useRef(config.tts);
  const nomenRef = useRef(config.nomenclature);

  // 24/7 unattended-mode features (configurable in admin)
  const kioskCfg = config.kiosk || {};
  useWakeLock(kioskCfg.wakeLock !== false);
  useScheduledReload(kioskCfg.autoReloadTime);

  // Connection heartbeat: green when poll succeeded recently, red after 8s of silence.
  // Tracks polling itself (not data changes), so it stays green during long quiet periods.
  const [connected, setConnected] = useState(true);
  useEffect(() => {
    if (!lastPolledAt) return undefined;
    setConnected(true);
    const id = window.setTimeout(() => setConnected(false), 8000);
    return () => window.clearTimeout(id);
  }, [lastPolledAt]);

  useEffect(() => {
    ttsRef.current = config.tts;
    nomenRef.current = config.nomenclature;
  }, [config.tts, config.nomenclature]);

  // Trigger announcement whenever updated_at changes (polling or broadcast)
  useEffect(() => {
    const unsub = onStateChange((nextState) => {
      const ticket = nextState?.currentTicket;
      if (!ticket) return;
      playChime();
      setTimeout(() => {
        speak(
          buildAnnouncement(ticket.code, ticket.desk, ttsRef.current.lang, nomenRef.current),
          ttsRef.current
        );
      }, 700);
      if (flashRef.current) {
        flashRef.current.classList.remove("flash-pulse");
        void flashRef.current.offsetWidth;
        flashRef.current.classList.add("flash-pulse");
      }
    });
    return unsub;
  }, [onStateChange]);

  const { colors, layout, media, ticker } = config;
  const mediaFirst = layout === LAYOUT_MODES.MEDIA_LEFT;

  // Layouts where the password zone is small/narrow → compact typography
  const customCfg = config.layoutCustom || {};
  const isCustomPip = layout === LAYOUT_MODES.CUSTOM && customCfg.orientation === "pip" && customCfg.pipTarget === "media";
  const isPasswordCompact =
    layout === LAYOUT_MODES.PIP_PASSWORD_BR || isCustomPip;

  // Layouts where the password zone is wide and short (stacked layouts) → put current+history side-by-side
  const isCustomVertical = layout === LAYOUT_MODES.CUSTOM && customCfg.orientation === "vertical";
  const isPasswordWide =
    !isPasswordCompact &&
    (layout === LAYOUT_MODES.PASSWORD_TOP ||
      layout === LAYOUT_MODES.MEDIA_TOP ||
      isCustomVertical);

  // PiP layouts: show only the current ticket being called (no header, no history)
  const isPipMode =
    layout === LAYOUT_MODES.PIP_MEDIA_BR ||
    layout === LAYOUT_MODES.PIP_PASSWORD_BR ||
    (layout === LAYOUT_MODES.CUSTOM && customCfg.orientation === "pip");

  const PasswordZoneEl = (
    <PasswordZone
      state={state}
      colors={colors}
      nomenclature={config.nomenclature}
      flashRef={flashRef}
      compact={isPasswordCompact}
      wideLayout={isPasswordWide}
      hideExtras={isPipMode}
    />
  );

  const MediaSection = (
    <section className="relative overflow-hidden transition-all duration-500 w-full h-full" data-testid="media-section">
      <MediaZone
        media={media}
        bgColor={colors.mediaBg}
        bgOpacity={colors.mediaBgOpacity ?? 1}
        bgImage={colors.mediaBgImage || ""}
        silentByDefault={!!kioskCfg.silentByDefault}
      />
    </section>
  );

  const renderLayout = () => {
    // CARDS layout — priority + 3 normal cards + media + clock
    if (layout === LAYOUT_MODES.CARDS) {
      return (
        <CardsLayout
          state={state}
          config={config}
          colors={colors}
          media={media}
          flashRef={flashRef}
          silentByDefault={!!kioskCfg.silentByDefault}
        />
      );
    }

    // Custom layout — computed from config.layoutCustom
    if (layout === LAYOUT_MODES.CUSTOM) {
      const c = config.layoutCustom || {};
      const ratio = Math.min(80, Math.max(20, c.passwordRatio || 50));
      const pos = c.passwordPosition || "left";
      const orientation = c.orientation || "horizontal";

      if (orientation === "pip") {
        const target = c.pipTarget === "password" ? "password" : "media";
        const pipSize = Math.min(45, Math.max(10, c.pipSize || 25));
        const corner = pos.length === 2 ? pos : "br"; // tl|tr|bl|br
        const corners = {
          tl: "top-4 left-4",
          tr: "top-4 right-4",
          bl: "bottom-4 left-4",
          br: "bottom-4 right-4",
        };
        const Big = target === "media" ? PasswordZoneEl : MediaSection;
        const Small = target === "media" ? MediaSection : PasswordZoneEl;
        return (
          <div className="relative h-full w-full">
            <div className="absolute inset-0">{Big}</div>
            <div
              className={`absolute ${corners[corner] || corners.br} shadow-2xl border-2 border-foreground/20 overflow-hidden`}
              style={{ width: `${pipSize}vw`, height: `${pipSize * 0.6}vw`, minWidth: 200, minHeight: 120 }}
            >
              {Small}
            </div>
          </div>
        );
      }

      if (orientation === "vertical") {
        const passwordOnTop = pos === "top";
        return (
          <div className="flex flex-col h-full w-full">
            <div style={{ height: `${passwordOnTop ? ratio : 100 - ratio}%` }} className="relative">
              {passwordOnTop ? PasswordZoneEl : MediaSection}
            </div>
            <div style={{ height: `${passwordOnTop ? 100 - ratio : ratio}%` }} className="relative border-t border-border/40">
              {passwordOnTop ? MediaSection : PasswordZoneEl}
            </div>
          </div>
        );
      }

      // horizontal (default)
      const passwordLeft = pos !== "right";
      return (
        <div className="flex flex-row h-full w-full">
          <div style={{ width: `${passwordLeft ? ratio : 100 - ratio}%` }} className="relative">
            {passwordLeft ? PasswordZoneEl : MediaSection}
          </div>
          <div style={{ width: `${passwordLeft ? 100 - ratio : ratio}%` }} className="relative border-l border-border/40">
            {passwordLeft ? MediaSection : PasswordZoneEl}
          </div>
        </div>
      );
    }

    // Fullscreen modes
    if (layout === LAYOUT_MODES.FULLSCREEN_MEDIA)
      return <div className="h-full w-full">{MediaSection}</div>;
    if (layout === LAYOUT_MODES.FULLSCREEN_PASSWORD)
      return <div className="h-full w-full">{PasswordZoneEl}</div>;

    // Vertical stacks
    if (layout === LAYOUT_MODES.PASSWORD_TOP) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="h-1/2 relative">{PasswordZoneEl}</div>
          <div className="h-1/2 relative border-t border-border/40">{MediaSection}</div>
        </div>
      );
    }
    if (layout === LAYOUT_MODES.MEDIA_TOP) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="h-1/2 relative">{MediaSection}</div>
          <div className="h-1/2 relative border-t border-border/40">{PasswordZoneEl}</div>
        </div>
      );
    }

    // 70/30 splits
    if (layout === LAYOUT_MODES.PASSWORD_70_RIGHT) {
      return (
        <div className="flex flex-row h-full w-full">
          <div className="w-[30%] relative">{MediaSection}</div>
          <div className="w-[70%] relative border-l border-border/40">{PasswordZoneEl}</div>
        </div>
      );
    }
    if (layout === LAYOUT_MODES.MEDIA_70_RIGHT) {
      return (
        <div className="flex flex-row h-full w-full">
          <div className="w-[30%] relative border-r border-border/40">{PasswordZoneEl}</div>
          <div className="w-[70%] relative">{MediaSection}</div>
        </div>
      );
    }

    // Picture-in-picture presets
    if (layout === LAYOUT_MODES.PIP_MEDIA_BR) {
      return (
        <div className="relative h-full w-full">
          <div className="absolute inset-0">{PasswordZoneEl}</div>
          <div
            className="absolute bottom-4 right-4 shadow-2xl border-2 border-foreground/20 overflow-hidden"
            style={{ width: "25vw", height: "15vw", minWidth: 240, minHeight: 140 }}
          >
            {MediaSection}
          </div>
        </div>
      );
    }
    if (layout === LAYOUT_MODES.PIP_PASSWORD_BR) {
      return (
        <div className="relative h-full w-full">
          <div className="absolute inset-0">{MediaSection}</div>
          <div
            className="absolute bottom-4 right-4 shadow-2xl border-2 border-foreground/20 overflow-hidden bg-background"
            style={{ width: "28vw", height: "16vw", minWidth: 260, minHeight: 160 }}
          >
            {PasswordZoneEl}
          </div>
        </div>
      );
    }

    // Default presets MEDIA_LEFT / MEDIA_RIGHT (legacy)
    return mediaFirst ? (
      <div className="flex flex-row h-full w-full">
        <div className="w-[58%] relative">{MediaSection}</div>
        <div className="w-[42%] relative border-l border-border/40">{PasswordZoneEl}</div>
      </div>
    ) : (
      <div className="flex flex-row h-full w-full">
        <div className="w-[42%] relative border-r border-border/40">{PasswordZoneEl}</div>
        <div className="w-[58%] relative">{MediaSection}</div>
      </div>
    );
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background flex flex-col">
      {/* 24/7 connection heartbeat — tiny dot in the very top-left corner */}
      {kioskCfg.heartbeat !== false && (
        <div
          className="absolute top-2 left-2 z-50 flex items-center gap-1.5 px-2 py-1 bg-background/80 backdrop-blur-sm border border-foreground/10 text-[9px] uppercase tracking-[0.3em] opacity-60 hover:opacity-100 transition-opacity"
          data-testid="tv-heartbeat"
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              connected ? "bg-emerald-500 animate-pulse" : "bg-red-500"
            }`}
          />
          <span className="text-foreground/70">{connected ? "ON" : "OFF"}</span>
        </div>
      )}
      <div className="flex-1 overflow-hidden">{renderLayout()}</div>
      <Ticker
        messages={ticker}
        bgColor={colors.tickerBg}
        textColor={colors.tickerText}
        bgOpacity={colors.tickerBgOpacity ?? 1}
        showClock={config.showClock !== false && layout !== LAYOUT_MODES.CARDS}
      />
    </div>
  );
};

export default TVDisplay;
